/**
 * The conversation with a forge: turn history, the live turn's stream, and
 * the screenshots + tool events it produces.
 *
 * NOT React Query: this is the WebSocket-driven part (docs/react-query-patterns.md
 * "Do NOT use React Query for"). Sending a turn is `POST /turns` → the
 * response's `requestId` → `useStreaming` subscribes; the runtime's events
 * come back as `token` / `activity` / `tool_call` / `tool_result` /
 * `forge_screenshot` / `error` / `complete` frames on that id.
 *
 * Only the prompt is persisted server-side (forge_turns has no transcript),
 * so history renders each prior turn as the user's prompt plus a one-line
 * outcome bubble; the streamed assistant text lives for the page session.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { WEBSOCKET_ENDPOINT } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { useStreaming } from '@/lib/hooks/useStreaming';
import { addBreadcrumb, captureError } from '@/lib/telemetry';
import { forgeQueryKeys } from '../api/queryKeys';
import type { ForgeMessage, ForgeScreenshot, ForgeToolEvent, ForgeTurnSummary } from '../types';

/** Screenshots kept per page session — older ones scroll off. */
const MAX_SCREENSHOTS = 24;
/** A turn's tool events kept in memory (Claude Code turns can run hundreds). */
const MAX_TOOL_EVENTS_PER_TURN = 400;

export interface UseForgeTurnResult {
  messages: ForgeMessage[];
  isProcessing: boolean;
  streamingText: string;
  activity: string | null;
  screenshots: ForgeScreenshot[];
  /** Tool events of the live turn. */
  pendingToolEvents: ForgeToolEvent[];
  /** Tool events of completed turns, keyed by the assistant bubble's timestamp. */
  toolEventHistory: Record<string, ForgeToolEvent[]>;
  lastFailure: { code: string; message: string; retryable: boolean } | null;
  sendTurn: (prompt: string) => Promise<void>;
  stopTurn: () => Promise<void>;
  retry: () => Promise<void>;
  clearScreenshots: () => void;
}

function outcomeLine(turn: ForgeTurnSummary): string {
  const cost = turn.costCents > 0 ? ` · ${(turn.costCents / 100).toFixed(2)} credits` : '';
  const tools = turn.toolCalls > 0 ? ` · ${turn.toolCalls} tool calls` : '';
  switch (turn.status) {
    case 'completed':
      return `Turn completed${tools}${cost}.`;
    case 'stopped':
      return `Turn stopped${tools}${cost}.`;
    case 'failed':
      return `Turn failed${tools}${cost}.`;
    case 'expired':
      return `Turn expired (hit the wall-clock limit)${tools}${cost}.`;
    default:
      return 'Turn in progress…';
  }
}

export function useForgeTurn(forgeId: string | null): UseForgeTurnResult {
  const queryClient = useQueryClient();
  const authToken = authApi.getIdToken();

  const [requestId, setRequestId] = useState<string | null>(null);
  const [liveMessages, setLiveMessages] = useState<ForgeMessage[]>([]);
  const [activity, setActivity] = useState<string | null>(null);
  const [screenshots, setScreenshots] = useState<ForgeScreenshot[]>([]);
  const [pendingToolEvents, setPendingToolEvents] = useState<ForgeToolEvent[]>([]);
  const [toolEventHistory, setToolEventHistory] = useState<Record<string, ForgeToolEvent[]>>({});
  const [lastFailure, setLastFailure] = useState<UseForgeTurnResult['lastFailure']>(null);
  const [isSending, setIsSending] = useState(false);

  const streamingTextRef = useRef('');
  const lastPromptRef = useRef<string | null>(null);
  // toolUseId → tool name, so a result bubble can be named (forge results
  // carry only the id; see websocket.ts ToolResultMessage).
  const toolNamesRef = useRef<Map<string, string>>(new Map());
  // Turns whose requestId we have already adopted from history (page reload
  // mid-turn) — never re-subscribe to the same one twice.
  const adoptedOpenTurnRef = useRef<string | null>(null);

  const turnsQuery = useQuery({
    queryKey: forgeQueryKeys.turns(forgeId ?? ''),
    queryFn: () => api.listForgeTurns(forgeId!),
    enabled: !!forgeId,
  });
  const turns = turnsQuery.data?.turns;

  // Reload mid-turn: an `open` turn in history is still streaming — subscribe
  // to its requestId so the page picks the stream back up.
  useEffect(() => {
    if (!turns || requestId) return;
    const open = turns.find((t) => t.status === 'open');
    if (open && adoptedOpenTurnRef.current !== open.requestId) {
      adoptedOpenTurnRef.current = open.requestId;
      streamingTextRef.current = '';
      setRequestId(open.requestId);
    }
  }, [turns, requestId]);

  const finishTurn = useCallback(
    (outcome: { failed?: { code: string; message: string } }) => {
      setActivity(null);
      const stamp = new Date().toISOString();
      const text = streamingTextRef.current.trim();
      streamingTextRef.current = '';
      setPendingToolEvents((events) => {
        if (events.length > 0) setToolEventHistory((h) => ({ ...h, [stamp]: events }));
        return [];
      });
      if (text || outcome.failed) {
        setLiveMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: text || `Turn failed: ${outcome.failed?.message ?? 'unknown error'}`,
            timestamp: stamp,
          },
        ]);
      }
      setRequestId(null);
      setIsSending(false);
      // The turn row (status/cost/toolCalls) and the forge (lastActiveAt,
      // pendingCommand) both moved server-side.
      if (forgeId) {
        void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.detail(forgeId) });
      }
    },
    [forgeId, queryClient],
  );

  const streaming = useStreaming({
    requestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!authToken,
    onTextUpdate: useCallback((_nodeId: string, text: string) => {
      streamingTextRef.current += text;
    }, []),
    onActivity: useCallback((label: string) => setActivity(label), []),
    onToolCall: useCallback((toolName: string, toolInput: unknown, toolUseId?: string) => {
      if (toolUseId) toolNamesRef.current.set(toolUseId, toolName);
      setPendingToolEvents((events) =>
        [
          ...events,
          {
            type: 'tool_call' as const,
            timestamp: new Date().toISOString(),
            data: { toolName, toolInput },
          },
        ].slice(-MAX_TOOL_EVENTS_PER_TURN),
      );
    }, []),
    onToolResult: useCallback(
      (toolName: string, toolResult: unknown, isError: boolean, toolUseId?: string) => {
        const name = toolName || (toolUseId ? toolNamesRef.current.get(toolUseId) : undefined);
        setPendingToolEvents((events) =>
          [
            ...events,
            {
              type: 'tool_result' as const,
              timestamp: new Date().toISOString(),
              data: { toolName: name ?? 'tool', toolResult, isError },
            },
          ].slice(-MAX_TOOL_EVENTS_PER_TURN),
        );
      },
      [],
    ),
    onForgeScreenshot: useCallback(
      (payload: { requestId: string; path: string; contentBase64: string }) => {
        setScreenshots((prev) =>
          [
            ...prev,
            {
              path: payload.path,
              dataUrl: `data:image/png;base64,${payload.contentBase64}`,
              requestId: payload.requestId,
              receivedAt: new Date().toISOString(),
            },
          ].slice(-MAX_SCREENSHOTS),
        );
      },
      [],
    ),
    onError: useCallback(
      (code: string, message: string) => {
        captureError(new Error(message), { code, forgeId, surface: 'forge-turn' });
        // Transport-level failures are retryable by resending the same prompt;
        // a turn the platform closed as failed is not (the runtime already ran).
        const retryable = code !== 'FORGE_TURN_FAILED';
        setLastFailure({ code, message, retryable });
        if (!retryable) finishTurn({ failed: { code, message } });
      },
      [finishTurn, forgeId],
    ),
    onComplete: useCallback(() => {
      finishTurn({});
    }, [finishTurn]),
  });

  const sendTurn = useCallback(
    async (prompt: string) => {
      if (!forgeId || isSending || requestId) return;
      const trimmed = prompt.trim();
      if (!trimmed) return;
      setIsSending(true);
      setLastFailure(null);
      lastPromptRef.current = trimmed;
      streamingTextRef.current = '';
      streaming.reset();
      setLiveMessages((prev) => [
        ...prev,
        { role: 'user', content: trimmed, timestamp: new Date().toISOString() },
      ]);
      try {
        const result = await api.sendForgeTurn(forgeId, { prompt: trimmed });
        addBreadcrumb('forge', 'turn opened', { forgeId, turnId: result.turnId });
        setRequestId(result.requestId);
        void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.detail(forgeId) });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send';
        setLastFailure({ code: 'SEND_FAILED', message, retryable: true });
        setIsSending(false);
      }
    },
    [forgeId, isSending, requestId, streaming, queryClient],
  );

  const stopTurn = useCallback(async () => {
    if (!forgeId) return;
    try {
      await api.stopForgeTurn(forgeId);
      addBreadcrumb('forge', 'stop requested', { forgeId });
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), {
        forgeId,
        surface: 'forge-stop-turn',
      });
    }
  }, [forgeId]);

  const retry = useCallback(async () => {
    const prompt = lastPromptRef.current;
    if (!prompt) return;
    // Drop the optimistic user bubble the failed send left behind.
    setLiveMessages((prev) => {
      const last = prev[prev.length - 1];
      return last?.role === 'user' && last.content === prompt ? prev.slice(0, -1) : prev;
    });
    setIsSending(false);
    await sendTurn(prompt);
  }, [sendTurn]);

  const clearScreenshots = useCallback(() => setScreenshots([]), []);

  // History bubbles: every persisted turn as prompt + outcome, EXCEPT the one
  // currently open (its prompt is already in liveMessages, or it is being
  // picked up from a reload in which case there is no live bubble yet).
  const historyMessages = useMemo<ForgeMessage[]>(() => {
    if (!turns) return [];
    const liveStamps = new Set(liveMessages.map((m) => m.content));
    return turns
      .filter((t) => t.status !== 'open' || !liveStamps.has(t.prompt))
      .flatMap((t) => {
        const started = t.startedAt ?? new Date(0).toISOString();
        const out: ForgeMessage[] = [{ role: 'user', content: t.prompt, timestamp: started }];
        if (t.status !== 'open') {
          out.push({ role: 'assistant', content: outcomeLine(t), timestamp: t.endedAt ?? started });
        }
        return out;
      });
  }, [turns, liveMessages]);

  const messages = useMemo(() => {
    // History is ascending by start; live bubbles come after.
    const history = [...historyMessages].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    // A turn opened in this page session is in BOTH lists once the turns
    // query refetches — keep the live copy (it carries the streamed text).
    const liveUserPrompts = new Set(
      liveMessages.filter((m) => m.role === 'user').map((m) => m.content),
    );
    const dedupedHistory: ForgeMessage[] = [];
    for (let i = 0; i < history.length; i += 1) {
      const m = history[i];
      if (m.role === 'user' && liveUserPrompts.has(m.content)) {
        // Skip this prompt and its outcome line.
        if (history[i + 1]?.role === 'assistant') i += 1;
        continue;
      }
      dedupedHistory.push(m);
    }
    return [...dedupedHistory, ...liveMessages];
  }, [historyMessages, liveMessages]);

  const streamingText = streaming.streamingText.forge ?? '';

  return {
    messages,
    isProcessing: isSending || requestId !== null,
    streamingText,
    activity,
    screenshots,
    pendingToolEvents,
    toolEventHistory,
    lastFailure,
    sendTurn,
    stopTurn,
    retry,
    clearScreenshots,
  };
}
